import { useState } from "react";

import dayjs from "@eisox/dayjs";
import { ChevronLeftIcon, ChevronRightIcon } from "@eisox/icons";

import { cn } from "..";
import { monthFromDate, monthRangeEnd, monthRangeStart } from "./month-range";

interface Month {
  number: number;
  name: string;
  yearOffset: number;
}

const MONTHS: Month[][] = [
  [
    { number: 0, name: dayjs().month(0).format("MMM"), yearOffset: 0 },
    { number: 1, name: dayjs().month(1).format("MMM"), yearOffset: 0 },
    { number: 2, name: dayjs().month(2).format("MMM"), yearOffset: 0 },
    { number: 3, name: dayjs().month(3).format("MMM"), yearOffset: 0 },
    { number: 0, name: dayjs().month(0).format("MMM"), yearOffset: 1 },
    { number: 1, name: dayjs().month(1).format("MMM"), yearOffset: 1 },
    { number: 2, name: dayjs().month(2).format("MMM"), yearOffset: 1 },
    { number: 3, name: dayjs().month(3).format("MMM"), yearOffset: 1 },
  ],
  [
    { number: 4, name: dayjs().month(4).format("MMM"), yearOffset: 0 },
    { number: 5, name: dayjs().month(5).format("MMM"), yearOffset: 0 },
    { number: 6, name: dayjs().month(6).format("MMM"), yearOffset: 0 },
    { number: 7, name: dayjs().month(7).format("MMM"), yearOffset: 0 },
    { number: 4, name: dayjs().month(4).format("MMM"), yearOffset: 1 },
    { number: 5, name: dayjs().month(5).format("MMM"), yearOffset: 1 },
    { number: 6, name: dayjs().month(6).format("MMM"), yearOffset: 1 },
    { number: 7, name: dayjs().month(7).format("MMM"), yearOffset: 1 },
  ],
  [
    { number: 8, name: dayjs().month(8).format("MMM"), yearOffset: 0 },
    { number: 9, name: dayjs().month(9).format("MMM"), yearOffset: 0 },
    { number: 10, name: dayjs().month(10).format("MMM"), yearOffset: 0 },
    { number: 11, name: dayjs().month(11).format("MMM"), yearOffset: 0 },
    { number: 8, name: dayjs().month(8).format("MMM"), yearOffset: 1 },
    { number: 9, name: dayjs().month(9).format("MMM"), yearOffset: 1 },
    { number: 10, name: dayjs().month(10).format("MMM"), yearOffset: 1 },
    { number: 11, name: dayjs().month(11).format("MMM"), yearOffset: 1 },
  ],
];

interface MonthRangeCalendarProps {
  selectedMonthRange?: { start: Date; end: Date };
  onStartMonthSelect?: (date: Date) => void;
  onMonthRangeSelect?: ({ start, end }: { start: Date; end: Date }) => void;
  onYearForward?: () => void;
  onYearBackward?: () => void;
  callbacks?: {
    yearLabel?: (year: number) => string;
    monthLabel?: (month: Month) => string;
  };
  minDate?: Date;
  maxDate?: Date;
}

function MonthRangePicker({
  onMonthRangeSelect,
  onStartMonthSelect,
  callbacks,
  selectedMonthRange,
  onYearBackward,
  onYearForward,
  minDate,
  maxDate,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & MonthRangeCalendarProps) {
  return (
    <div className={cn("p-3 min-w-100", className)} {...props}>
      <div className="space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 flex flex-col">
        <div className="w-full">
          <MonthRangeCalendar
            onMonthRangeSelect={onMonthRangeSelect}
            onStartMonthSelect={onStartMonthSelect}
            callbacks={callbacks}
            selectedMonthRange={selectedMonthRange}
            onYearBackward={onYearBackward}
            onYearForward={onYearForward}
            minDate={minDate}
            maxDate={maxDate}
          ></MonthRangeCalendar>
        </div>
      </div>
    </div>
  );
}

function MonthRangeCalendar({
  selectedMonthRange,
  onStartMonthSelect,
  onMonthRangeSelect,
  onYearForward,
  onYearBackward,
  callbacks,
  minDate,
  maxDate,
}: MonthRangeCalendarProps) {
  const initialStart = selectedMonthRange ? monthFromDate(selectedMonthRange.start) : undefined;
  const initialEnd = selectedMonthRange ? monthFromDate(selectedMonthRange.end) : undefined;
  const [startYear, setStartYear] = useState<number>(initialStart?.year ?? new Date().getFullYear());
  const [startMonth, setStartMonth] = useState<number>(initialStart?.month ?? new Date().getMonth());
  const [endYear, setEndYear] = useState<number>(initialEnd?.year ?? new Date().getFullYear());
  const [endMonth, setEndMonth] = useState<number>(initialEnd?.month ?? new Date().getMonth());
  const [rangePending, setRangePending] = useState<boolean>(false);
  const [endLocked, setEndLocked] = useState<boolean>(true);
  const [menuYear, setMenuYear] = useState<number>(startYear);

  if (minDate && maxDate && minDate > maxDate) minDate = maxDate;

  const minMonthBounds = minDate ? monthFromDate(minDate) : undefined;
  const maxMonthBounds = maxDate ? monthFromDate(maxDate) : undefined;

  const isMonthBeforeMin = (year: number, month: number) =>
    minMonthBounds !== undefined &&
    (year < minMonthBounds.year || (year === minMonthBounds.year && month < minMonthBounds.month));

  const isMonthAfterMax = (year: number, month: number) =>
    maxMonthBounds !== undefined &&
    (year > maxMonthBounds.year || (year === maxMonthBounds.year && month > maxMonthBounds.month));

  const handleYearBackward = () => {
    setMenuYear(prev => prev - 1);
    onYearBackward?.();
  };

  const handleYearForward = () => {
    setMenuYear(prev => prev + 1);
    onYearForward?.();
  };

  const handleMonthSelect = (month: Month) => {
    if (rangePending) {
      if (
        menuYear + month.yearOffset < startYear ||
        (menuYear + month.yearOffset == startYear && month.number < startMonth)
      ) {
        setRangePending(true);
        setEndLocked(false);
        setStartMonth(month.number);
        setStartYear(menuYear + month.yearOffset);
        setEndYear(menuYear + month.yearOffset);
        setEndMonth(month.number);
        if (onStartMonthSelect) onStartMonthSelect(monthRangeStart(menuYear + month.yearOffset, month.number));
      } else {
        setRangePending(false);
        setEndLocked(true);
        // Event fire data selected

        if (onMonthRangeSelect)
          onMonthRangeSelect({
            start: monthRangeStart(startYear, startMonth),
            end: monthRangeEnd(menuYear + month.yearOffset, month.number),
          });
      }
    } else {
      setRangePending(true);
      setEndLocked(false);
      setStartMonth(month.number);
      setStartYear(menuYear + month.yearOffset);
      setEndYear(menuYear + month.yearOffset);
      setEndMonth(month.number);
      if (onStartMonthSelect) onStartMonthSelect(monthRangeStart(menuYear + month.yearOffset, month.number));
    }
  };

  return (
    <div className="gap-4 flex">
      <div className="space-y-4 min-w-100">
        <div className="pt-1 relative flex items-center justify-evenly">
          <div className="font-medium text-sm">{callbacks?.yearLabel?.(menuYear) ?? menuYear}</div>
          <div className="space-x-1 flex items-center">
            <button
              type="button"
              className="left-1 h-7 w-7 p-0 absolute inline-flex items-center justify-center"
              onClick={handleYearBackward}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="right-1 h-7 w-7 p-0 absolute inline-flex items-center justify-center"
              onClick={handleYearForward}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="font-medium text-sm">{callbacks?.yearLabel?.(menuYear + 1) ?? menuYear + 1}</div>
        </div>
        <table className="space-y-1 w-full border-collapse">
          <tbody>
            {MONTHS.map((monthRow, a) => {
              return (
                <tr key={"row-" + a} className="mt-2 flex w-full">
                  {monthRow.map((m, i) => {
                    return (
                      <td
                        key={m.number + "-" + m.yearOffset}
                        className={cn(
                          cn(
                            cn(
                              cn(
                                `h-10 p-0 first:has-aria-[selected]:rounded-l-md last:has-aria-[selected]:rounded-r-md [&:has([aria-selected].day-range-end)]:rounded-r-md relative w-1/4 text-center text-sm focus-within:relative focus-within:z-20 has-aria-[selected]:bg-primary-blue [&:has([aria-selected].day-outside)]:bg-primary-blue/50`,
                                (menuYear + m.yearOffset > startYear ||
                                  (menuYear + m.yearOffset == startYear && m.number > startMonth)) &&
                                  (menuYear + m.yearOffset < endYear ||
                                    (menuYear + m.yearOffset == endYear && m.number < endMonth)) &&
                                  (rangePending || endLocked)
                                  ? // oxlint-disable-next-line tailwindcss/no-hardcoded-colors
                                    "bg-[rgba(0,41,255,0.07)] text-primary-blue"
                                  : "",
                              ),
                              menuYear + m.yearOffset == startYear &&
                                m.number == startMonth &&
                                (rangePending || endLocked)
                                ? "rounded-l-md bg-primary-blue text-primary-white"
                                : "",
                            ),
                            menuYear + m.yearOffset == endYear &&
                              m.number == endMonth &&
                              (rangePending || endLocked) &&
                              menuYear + m.yearOffset >= startYear &&
                              m.number >= startMonth
                              ? "rounded-r-md bg-primary-blue text-primary-white"
                              : "",
                          ),
                          i == 3 ? "mr-2" : i == 4 ? "ml-2" : "",
                        )}
                        onMouseEnter={() => {
                          if (rangePending && !endLocked) {
                            setEndYear(menuYear + m.yearOffset);
                            setEndMonth(m.number);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className={"p-0 font-normal h-full w-full aria-selected:opacity-100"}
                          onClick={() => handleMonthSelect(m)}
                          disabled={
                            isMonthAfterMax(menuYear + m.yearOffset, m.number) ||
                            isMonthBeforeMin(menuYear + m.yearOffset, m.number)
                          }
                        >
                          {callbacks?.monthLabel ? callbacks.monthLabel(m) : m.name}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

MonthRangePicker.displayName = "MonthRangePicker";

export { MonthRangePicker };
